import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
} from "@mui/material";
import {
  Check as CheckIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";
import axios from "axios";

function PlanSelector({ t, plans, meteredPrices, smsBilledPerSegment, currentPlan, isTrialActive, onCheckoutStarted, onPlanUpdated }) {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fi-FI", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const handleBillingPeriodChange = (event, newPeriod) => {
    if (newPeriod) {
      setBillingPeriod(newPeriod);
    }
  };

  const handleSelectPlan = async (planId) => {
    try {
      setLoading(planId);
      setError(null);
      setSuccess(null);

      const response = await axios.post("/api/payments/checkout/", {
        plan_id: planId,
        billing_period: billingPeriod,
      });

      // Case 1: Redirect to Stripe checkout (new subscription or trial upgrade)
      if (response.data.checkout_url) {
        if (onCheckoutStarted) {
          onCheckoutStarted();
        }
        window.location.href = response.data.checkout_url;
      }
      // Case 2: Plan updated directly (existing subscription)
      else if (response.data.success) {
        setSuccess(response.data.message || t("Plan updated successfully!"));
        if (onPlanUpdated) {
          onPlanUpdated();
        }
        // Reload page after short delay to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(
        err.response?.data?.error || t("Failed to start checkout. Please try again.")
      );
    } finally {
      setLoading(null);
    }
  };

  const getPrice = (plan) => {
    return billingPeriod === "yearly" ? plan.price_yearly : plan.price_monthly;
  };

  const getMonthlyEquivalent = (plan) => {
    if (billingPeriod === "yearly") {
      return plan.price_yearly / 12;
    }
    return plan.price_monthly;
  };

  const isCurrentPlan = (plan) => {
    return currentPlan && currentPlan.id === plan.id;
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Billing Period Toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <ToggleButtonGroup
          value={billingPeriod}
          exclusive
          onChange={handleBillingPeriodChange}
          aria-label={t("Billing period")}
        >
          <ToggleButton value="monthly" aria-label={t("Monthly")}>
            {t("Monthly")}
          </ToggleButton>
          <ToggleButton value="yearly" aria-label={t("Yearly")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {t("Yearly")}
              <Chip
                label={t("Save 17%")}
                size="small"
                color="success"
                sx={{ ml: 1 }}
              />
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Plan Cards */}
      <Grid container spacing={3} justifyContent="center">
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <Card
              variant={plan.is_highlighted ? "elevation" : "outlined"}
              elevation={plan.is_highlighted ? 8 : 0}
              sx={{
                height: "100%",
                position: "relative",
                border: plan.is_highlighted ? "2px solid" : undefined,
                borderColor: plan.is_highlighted ? "primary.main" : undefined,
                display: "flex",
                flexDirection: "column",
                overflow: "visible",
              }}
            >
              {/* Most Popular Badge */}
              {plan.is_highlighted && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 1,
                  }}
                >
                  <Chip
                    icon={<StarIcon />}
                    label={t("Most Popular")}
                    color="primary"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}

              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  pt: plan.is_highlighted ? 4 : 3,
                }}
              >
                {/* Plan Name */}
                <Typography
                  variant="h5"
                  fontWeight={600}
                  textAlign="center"
                  gutterBottom
                >
                  {plan.display_name}
                </Typography>

                {/* Description */}
                {plan.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 2, minHeight: 40 }}
                  >
                    {plan.description}
                  </Typography>
                )}

                {/* Price */}
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography variant="h3" fontWeight={700} color="primary">
                    {formatCurrency(getMonthlyEquivalent(plan))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    / {t("month")}
                  </Typography>
                  {billingPeriod === "yearly" && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {formatCurrency(getPrice(plan))} {t("billed yearly")}
                    </Typography>
                  )}
                </Box>

                {/* Seats */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 3,
                    p: 2,
                    backgroundColor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {plan.is_unlimited_seats
                      ? t("Unlimited users")
                      : t("Up to {{count}} users", { count: plan.max_seats })}
                  </Typography>
                </Box>

                {/* Features */}
                {Array.isArray(plan.features) && plan.features.length > 0 && (
                  <Box sx={{ mb: 3, flexGrow: 1 }}>
                    {plan.features.map((feature, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <CheckIcon color="success" fontSize="small" />
                        <Typography variant="body2">
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Spacer */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Action Button */}
                <Box sx={{ mt: "auto" }}>
                  {isCurrentPlan(plan) && !isTrialActive ? (
                    <Button
                      variant="outlined"
                      fullWidth
                      disabled
                      size="large"
                    >
                      {t("Current Plan")}
                    </Button>
                  ) : (
                    <Button
                      variant={plan.is_highlighted ? "contained" : "outlined"}
                      fullWidth
                      size="large"
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={loading !== null}
                    >
                      {loading === plan.id ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : isTrialActive ? (
                        t("Upgrade Now")
                      ) : isCurrentPlan(plan) ? (
                        t("Change Billing")
                      ) : (
                        t("Select Plan")
                      )}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Metered Billing Notice */}
      <Box sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {t("All plans include metered billing for SMS and e-invoices.")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(
            smsBilledPerSegment
              ? "SMS: {{smsPrice}}/segment, E-invoices: {{einvoicePrice}}/invoice"
              : "SMS: {{smsPrice}}/message, E-invoices: {{einvoicePrice}}/invoice",
            {
              smsPrice: formatCurrency(meteredPrices?.sms_sent ?? 0.10),
              einvoicePrice: formatCurrency(meteredPrices?.einvoice_sent ?? 0.50),
            }
          )}
        </Typography>
      </Box>

      {/* VAT Notice */}
      <Box sx={{ mt: 2, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          {t("All prices exclude VAT. VAT will be added at checkout.")}
        </Typography>
      </Box>
    </Box>
  );
}

export default withTranslation()(PlanSelector);
